import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfHour,
  startOfMonth,
  startOfWeek,
  subDays,
  subHours,
  subMinutes,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns"
import {
  startOfUTCDay,
  endOfUTCDay,
  startOfUTCWeek,
  endOfUTCWeek,
  startOfUTCMonth,
  endOfUTCMonth,
  utcSubDays,
} from "@/src/utils/date-handler"

export enum DateRangeType {
  lastHour = "1hour",
  last3Hours = "3hours",
  last24Hours = "24hours",
  last3days = "3days",
  lastWeek = "7days",
  lastMonth = "30days",
  last32Days = "32days",
  previousWeek = "previous_week",
  previousMonth = "previous_month",
  previousYear = "previous_year",
  ytd = "ytd",
  mtd = "mtd",
  wtd = "wtd",
  today = "today",
  custom = "custom",
}

const getDateRangeItems = (timezone: "UTC" | "LOCAL" = "LOCAL") => {
  const now = new Date()
  const today = startOfHour(now)
  const todayDay = timezone === "UTC" ? startOfUTCDay(now) : startOfDay(now)
  const todayDayEnd = timezone === "UTC" ? endOfUTCDay(now) : endOfDay(now)
  const dateRangeItems: {
    name: string
    value: DateRangeType
    date: Date[]
  }[] = [
    {
      name: "Last 24 hours",
      value: DateRangeType.last24Hours,
      date: [subDays(today, 1), today],
    },
    {
      name: "Last 3 days", // 'name' is used for display in dropdown menu
      value: DateRangeType.last3days, // 'value' is attached to data queries as a parameter
      date: [subDays(todayDay, 2), todayDayEnd], // 3 days: today + 2 days back
    },
    {
      name: "Last 7 days", // 'name' is used for display in dropdown menu
      value: DateRangeType.lastWeek, // 'value' is attached to data queries as a parameter
      date: [subDays(todayDay, 6), todayDayEnd], // 7 days: today + 6 days back
    },
    {
      name: "Last 30 days",
      value: DateRangeType.lastMonth,
      date: [subDays(todayDay, 29), todayDayEnd],
    },
    {
      name: "Previous week",
      value: DateRangeType.previousWeek,
      date:
        timezone === "UTC"
          ? [
              startOfUTCWeek(new Date(todayDay.getTime() - 7 * 86_400_000)),
              endOfUTCWeek(new Date(todayDay.getTime() - 7 * 86_400_000)),
            ]
          : [
              startOfWeek(subWeeks(todayDay, 1), { weekStartsOn: 1 }),
              endOfDay(endOfWeek(subWeeks(todayDay, 1), { weekStartsOn: 1 })),
            ],
    },
    {
      name: "Previous month",
      value: DateRangeType.previousMonth,
      date:
        timezone === "UTC"
          ? (() => {
              const prevMonth = new Date(Date.UTC(todayDay.getUTCFullYear(), todayDay.getUTCMonth() - 1, 1))
              return [startOfUTCMonth(prevMonth), endOfUTCMonth(prevMonth)]
            })()
          : [startOfMonth(subMonths(todayDay, 1)), endOfDay(endOfMonth(subMonths(todayDay, 1)))],
    },
    {
      name: "WTD",
      value: DateRangeType.wtd,
      date: [
        timezone === "UTC" ? startOfUTCWeek(todayDay) : startOfWeek(todayDay, { weekStartsOn: 1 }),
        todayDayEnd,
      ],
    },
    {
      name: "MTD",
      value: DateRangeType.mtd,
      date: [timezone === "UTC" ? startOfUTCMonth(todayDay) : startOfMonth(todayDay), todayDayEnd],
    },
  ]
  return dateRangeItems
}

const getAuditDateRangeItems = (timezone: "UTC" | "LOCAL" = "LOCAL") => {
  const now = new Date()
  const today = now
  const todayDay = timezone === "UTC" ? startOfUTCDay(now) : startOfDay(now)
  const todayDayEnd = timezone === "UTC" ? endOfUTCDay(now) : endOfDay(now)
  const dateRangeItems: {
    name: string
    description: string
    date: Date[]
    /** true = only show when allowEditTime is true (sub-hour precision) */
    timeOnly?: boolean
    /** true = only show when allowEditTime is false (date-level precision) */
    dateOnly?: boolean
  }[] = [
    {
      name: "5m",
      description: "Last 5 minutes",
      date: [subMinutes(today, 5), today],
      timeOnly: true,
    },
    {
      name: "15m",
      description: "Last 15 minutes",
      date: [subMinutes(today, 15), today],
      timeOnly: true,
    },
    {
      name: "1h",
      description: "Last 1 hour",
      date: [subHours(today, 1), today],
      timeOnly: true,
    },
    {
      name: "3h",
      description: "Last 3 hours",
      date: [subHours(today, 3), today],
      timeOnly: true,
    },
    {
      name: "12h",
      description: "Last 12 hours",
      date: [subHours(today, 12), today],
      timeOnly: true,
    },
    {
      name: "1d",
      description: "Last 24 hours",
      date: [subHours(today, 24), today],
    },
    {
      name: "7d",
      description: "Last 7 days",
      date: [subDays(todayDay, 6), todayDayEnd],
    },
    {
      name: "30d",
      description: "Last 30 days",
      date: [subDays(todayDay, 29), todayDayEnd],
      dateOnly: true,
    },
    {
      name: "PW",
      description: "Previous week",
      date:
        timezone === "UTC"
          ? [
              startOfUTCWeek(new Date(todayDay.getTime() - 7 * 86_400_000)),
              endOfUTCWeek(new Date(todayDay.getTime() - 7 * 86_400_000)),
            ]
          : [
              startOfWeek(subWeeks(todayDay, 1), { weekStartsOn: 1 }),
              endOfDay(endOfWeek(subWeeks(todayDay, 1), { weekStartsOn: 1 })),
            ],
      dateOnly: true,
    },
    {
      name: "PM",
      description: "Previous month",
      date:
        timezone === "UTC"
          ? (() => {
              const prevMonth = new Date(Date.UTC(todayDay.getUTCFullYear(), todayDay.getUTCMonth() - 1, 1))
              return [startOfUTCMonth(prevMonth), endOfUTCMonth(prevMonth)]
            })()
          : [startOfMonth(subMonths(todayDay, 1)), endOfDay(endOfMonth(subMonths(todayDay, 1)))],
      dateOnly: true,
    },
    {
      name: "WTD",
      description: "Week to date",
      date: [
        timezone === "UTC" ? startOfUTCWeek(todayDay) : startOfWeek(todayDay, { weekStartsOn: 1 }),
        todayDayEnd,
      ],
      dateOnly: true,
    },
    {
      name: "MTD",
      description: "Month to date",
      date: [timezone === "UTC" ? startOfUTCMonth(todayDay) : startOfMonth(todayDay), todayDayEnd],
      dateOnly: true,
    },
  ]
  return dateRangeItems
}

const getLongRangeDateRangeItems = (timezone: "UTC" | "LOCAL" = "LOCAL") => {
  const now = new Date()
  const todayDay = timezone === "UTC" ? startOfUTCDay(now) : startOfDay(now)
  const todayDayEnd = timezone === "UTC" ? endOfUTCDay(now) : endOfDay(now)
  const daysAgo = (days: number) =>
    timezone === "UTC" ? utcSubDays(todayDay, days) : subDays(todayDay, days)
  const oneYearAgo =
    timezone === "UTC"
      ? new Date(Date.UTC(todayDay.getUTCFullYear() - 1, todayDay.getUTCMonth(), todayDay.getUTCDate()))
      : subYears(todayDay, 1)
  return [
    {
      name: "30d",
      description: "Last 30 days",
      date: [daysAgo(29), todayDayEnd],
    },
    {
      name: "90d",
      description: "Last 90 days",
      date: [daysAgo(89), todayDayEnd],
    },
    {
      name: "1y",
      description: "Last year",
      date: [oneYearAgo, todayDayEnd],
    },
  ]
}

export { getDateRangeItems, getAuditDateRangeItems, getLongRangeDateRangeItems }
